import type { Vector2, Vector5 } from "../../types/scene";

const UNSAFE_PASSTHROUGH_ATTRIBUTE_NAMES = new Set(["__proto__", "constructor", "prototype"]);

export const rgbaToHexString = (value?: string | null): string | null => {
  if (!value) return null;
  const parts = value
    .trim()
    .split(/\s+/)
    .map(Number)
    .filter((component) => Number.isFinite(component));
  if (parts.length < 3) return null;

  const clampComponent = (component: number) => {
    if (!Number.isFinite(component)) return 0;
    if (component <= 1) return Math.round(component * 255);
    return Math.round(component);
  };

  const toHex = (component: number) => {
    const clamped = Math.max(0, Math.min(255, clampComponent(component)));
    return clamped.toString(16).padStart(2, "0");
  };

  const alphaHex = parts.length >= 4 ? toHex(parts[3]) : "";
  return `#${toHex(parts[0])}${toHex(parts[1])}${toHex(parts[2])}${alphaHex}`;
};

export const parseSolimp = (solimp: string | null): Vector5 | undefined => {
  if (!solimp) return undefined;

  const solimpNums = solimp
    .trim()
    .split(/\s+/)
    .map(Number)
    .filter((n) => Number.isFinite(n));

  if (solimpNums.length < 3) return undefined;

  const defaults: Vector5 = [0.9, 0.95, 0.001, 0.5, 2];

  return [
    solimpNums[0] ?? defaults[0],
    solimpNums[1] ?? defaults[1],
    solimpNums[2] ?? defaults[2],
    solimpNums[3] ?? defaults[3],
    solimpNums[4] ?? defaults[4],
  ];
};

export const clampToRange = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

export const parseSolref = (raw: unknown): Vector2 | null => {
  if (!raw) return null;
  const nums = Array.isArray(raw)
    ? raw.map((v) => Number(v)).filter((n) => Number.isFinite(n))
    : String(raw)
        .trim()
        .split(/\s+/)
        .map((v) => Number(v))
        .filter((n) => Number.isFinite(n));
  if (nums.length < 2) return null;
  return [nums[0], nums[1]] as Vector2;
};

export const parseNumericAttribute = (raw: string | null): number[] | undefined => {
  if (!raw) return undefined;
  const values = raw
    .trim()
    .split(/\s+/)
    .map(Number)
    .filter((value) => Number.isFinite(value));
  return values.length > 0 ? values : undefined;
};

export const parsePassthroughAttributes = (
  node: Element,
  skipNames: string[] = []
): Record<string, string> | undefined => {
  const skip = new Set(skipNames);
  const attributes: Record<string, string> = {};
  Array.from(node.attributes || []).forEach((attr) => {
    if (
      !attr?.name ||
      skip.has(attr.name) ||
      UNSAFE_PASSTHROUGH_ATTRIBUTE_NAMES.has(attr.name.toLowerCase())
    ) {
      return;
    }
    attributes[attr.name] = attr.value;
  });
  return Object.keys(attributes).length > 0 ? attributes : undefined;
};
